import React from "react";

import {
  IPropertyFormDataType,
  PropertyFactFormTriState,
} from "../../services/forms/FormDataTypes";
import {
  BUILDING_USE_OPTIONS,
  OWNER_CLASS_LABELS,
  OWNER_CLASS_OPTIONS,
} from "../../services/incentiveMatchingVocabulary";
import { ZeosLabel } from "../Shared/ZeosLabel";

interface IProps {
  formData: IPropertyFormDataType;
  setFormData: React.Dispatch<React.SetStateAction<IPropertyFormDataType>>;
  disabled?: boolean;
}

const TRI_STATE_CHOICES: { value: PropertyFactFormTriState; label: string }[] = [
  { value: "", label: "Not answered" },
  { value: "Yes", label: "Yes" },
  { value: "No", label: "No" },
];

/**
 * The yes/no facts, in the order they are worth asking. Each hint says what the
 * question is asking in the owner's own terms; the gate it feeds is an
 * implementation detail and stays out of the UI.
 *
 * Owner's primary residence and Barn are deliberately absent: ZEOS-AI screens a
 * commercial and multifamily portfolio, so programmes tied to an individual
 * residence or to barn ownership are out of scope (PORT-005).
 */
const TRI_STATE_FACTS: {
  field: keyof IPropertyFormDataType;
  label: string;
  hint: string;
}[] = [
  {
    field: "isHistoric",
    label: "Historic building",
    hint: "Listed on a historic register, or inside a historic district.",
  },
  {
    field: "isAffordableHousing",
    label: "Affordable housing",
    hint: "Units are rent- or income-restricted under an affordable housing program.",
  },
  {
    field: "isIncomeQualified",
    label: "Income qualified",
    hint: "The building or its residents meet an income limit set by a program.",
  },
];

/**
 * The matching facts an owner states about their own building (IMPL-002 task F).
 *
 * These used to be reachable only from the super-admin panel, so the person
 * who knows the answers could not give them, and a screening run's guess was
 * the only way they ever got set. Answering here stores the value as
 * Confirmed, which is what stops a later run overwriting it.
 *
 * Leaving a question on "Not answered" is itself meaningful: unanswered is not
 * No. An unanswered fact leaves a programme flagged for verification rather
 * than excluded, so guessing here loses information.
 */
export const PropertyMatchingFactFields = (props: IProps) => {
  const { formData, setFormData, disabled = false } = props;

  const selectedUses = formData.buildingUses ?? [];

  const toggleUse = (id: string) => {
    setFormData((prev) => {
      const current = new Set(prev.buildingUses ?? []);
      if (current.has(id)) {
        current.delete(id);
      } else {
        current.add(id);
      }
      return { ...prev, buildingUses: Array.from(current) };
    });
  };

  return (
    <>
      <div className="col-span-6 px-2 pt-4">
        <ZeosLabel label="Owner class" htmlFor="property-fact-ownerClass" />
        <select
          id="property-fact-ownerClass"
          name="ownerClass"
          className="w-full border border-gray-300 rounded px-2 py-2 text-sm"
          value={formData.ownerClass ?? ""}
          onChange={(event) => {
            // Read the value here, not inside the updater: the updater runs
            // after React has already reverted this controlled select to its
            // value prop, so a lazy read sees the old value.
            const ownerClass = event.target.value;
            setFormData((prev) => ({ ...prev, ownerClass }));
          }}
          disabled={disabled}
        >
          <option value="">Not answered</option>
          {OWNER_CLASS_OPTIONS.map((id) => (
            <option key={id} value={id}>
              {OWNER_CLASS_LABELS[id] ?? id}
            </option>
          ))}
        </select>
        <p className="text-xs text-gray-500 mt-1">
          Who would apply. Separate from what the building is used for.
        </p>
      </div>

      <div className="col-span-6 px-2 pt-4">
        <ZeosLabel label="Building uses" />
        <div className="grid grid-cols-2 gap-1 border border-gray-200 rounded p-2 max-h-44 overflow-y-auto">
          {BUILDING_USE_OPTIONS.map((option) => (
            <label
              key={option.id}
              className="flex items-center gap-2 text-sm"
              title={option.note || undefined}
            >
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={selectedUses.includes(option.id)}
                onChange={() => toggleUse(option.id)}
                disabled={disabled}
              />
              <span>{option.label}</span>
            </label>
          ))}
        </div>
        <p className="text-xs text-gray-500 mt-1">
          Select every use present. Mixed use is its own choice and does not
          add the others.
        </p>
      </div>

      {TRI_STATE_FACTS.map((fact) => (
        <div key={fact.field} className="col-span-4 px-2">
          <ZeosLabel label={fact.label} htmlFor={`property-fact-${fact.field}`} />
          <select
            id={`property-fact-${fact.field}`}
            name={fact.field}
            className="w-full border border-gray-300 rounded px-2 py-2 text-sm"
            value={(formData[fact.field] as PropertyFactFormTriState) ?? ""}
            onChange={(event) => {
              const answer = event.target.value as PropertyFactFormTriState;
              setFormData((prev) => ({ ...prev, [fact.field]: answer }));
            }}
            disabled={disabled}
          >
            {TRI_STATE_CHOICES.map((choice) => (
              <option key={choice.value} value={choice.value}>
                {choice.label}
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-500 mt-1">{fact.hint}</p>
        </div>
      ))}
    </>
  );
};
